import React from 'react'
import {
  Create,
  SimpleForm,
  FileInput,
  FileField,
  required,
  Toolbar,
  SaveButton,
} from 'react-admin'
import { Box, Button, Typography } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'

const MAX_CONTRACT_FILES = 400
const MAX_TOTAL_UPLOAD_BYTES = 450 * 1024 * 1024

const validateContractFiles = (files) => {
  if (!files || files.length === 0) {
    return 'Please select at least one PDF'
  }
  if (files.length > MAX_CONTRACT_FILES) {
    return `Too many files selected. Maximum is ${MAX_CONTRACT_FILES}.`
  }
  const totalBytes = files.reduce(
    (sum, file) => sum + (file?.rawFile?.size || file?.size || 0),
    0
  )
  if (totalBytes > MAX_TOTAL_UPLOAD_BYTES) {
    return `Total upload is too large. Maximum is ${Math.floor(
      MAX_TOTAL_UPLOAD_BYTES / (1024 * 1024)
    )}MB.`
  }
  return undefined
}

const normalizeFiles = (value) => {
  if (!value) return []
  if (Array.isArray(value)) return value
  return [value]
}

const getTotalBytes = (files) =>
  normalizeFiles(files).reduce(
    (sum, file) => sum + (file?.rawFile?.size || file?.size || 0),
    0
  )

const SelectionSummary = ({ dropFeedback, onClearSelection }) => {
  const { setValue } = useFormContext()
  const files = normalizeFiles(useWatch({ name: 'files' }))
  const count = files.length
  const totalBytes = getTotalBytes(files)
  const totalMb = Math.ceil(totalBytes / (1024 * 1024))
  const hasFiles = count > 0

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {hasFiles
          ? `${count} file${count === 1 ? '' : 's'} selected (${totalMb}MB)`
          : 'No files selected. Drop PDFs into the upload area.'}
      </Typography>
      {dropFeedback && (
        <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
          {dropFeedback}
        </Typography>
      )}
      <Button
        size="small"
        sx={{ mt: 0.5 }}
        onClick={() => {
          setValue('files', [], { shouldValidate: true })
          onClearSelection()
        }}
        disabled={!hasFiles}
      >
        Clear Selection
      </Button>
    </Box>
  )
}

const ContractCreateToolbar = () => {
  const files = normalizeFiles(useWatch({ name: 'files' }))
  const count = files.length
  const totalBytes = getTotalBytes(files)
  const hasValidSelection =
    count > 0 &&
    count <= MAX_CONTRACT_FILES &&
    totalBytes <= MAX_TOTAL_UPLOAD_BYTES

  return (
    <Toolbar>
      <SaveButton disabled={!hasValidSelection} />
    </Toolbar>
  )
}

const UploadProgressNotice = () => {
  const [progress, setProgress] = React.useState({
    active: false,
    completed: 0,
    total: 0,
  })

  React.useEffect(() => {
    const handleProgress = (event) => {
      const { detail } = event
      if (!detail) return
      setProgress({
        active: Boolean(detail.active),
        completed: detail.completed || 0,
        total: detail.total || 0,
      })
    }

    window.addEventListener('contracts-upload-progress', handleProgress)
    return () =>
      window.removeEventListener('contracts-upload-progress', handleProgress)
  }, [])

  if (!progress.active) return null

  const pct =
    progress.total > 0
      ? Math.round((progress.completed / progress.total) * 100)
      : 0

  return (
    <Box
      sx={{
        mt: 1,
        mb: 1.5,
        px: 1.5,
        py: 1,
        border: '1px solid #dbeafe',
        borderRadius: 1.5,
        backgroundColor: '#eff6ff',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Uploading contracts: batch {progress.completed}/{progress.total} ({pct}
        %)
      </Typography>
      <Box
        sx={{
          mt: 1,
          width: '100%',
          height: 8,
          borderRadius: 999,
          backgroundColor: '#bfdbfe',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: `${pct}%`,
            height: '100%',
            backgroundColor: '#2563eb',
            transition: 'width 200ms linear',
          }}
        />
      </Box>
    </Box>
  )
}

export const ContractCreate = () => {
  const [dropFeedback, setDropFeedback] = React.useState('')

  return (
    <Create redirect="list">
      <SimpleForm toolbar={<ContractCreateToolbar />}>
        <FileInput
          source="files"
          label="Contract PDF(s)"
          accept="application/pdf"
          validate={[required(), validateContractFiles]}
          multiple
          options={{
            maxFiles: MAX_CONTRACT_FILES,
            onDropAccepted: () => setDropFeedback(''),
            onDropRejected: (rejections) => {
              if (!rejections || rejections.length === 0) {
                setDropFeedback('Some files could not be added.')
                return
              }
              const tooMany = rejections.some((r) =>
                (r.errors || []).some((e) => e.code === 'too-many-files')
              )
              const invalidType = rejections.some((r) =>
                (r.errors || []).some((e) => e.code === 'file-invalid-type')
              )
              if (tooMany) {
                setDropFeedback(
                  `Too many files dropped. Maximum is ${MAX_CONTRACT_FILES}.`
                )
                return
              }
              if (invalidType) {
                setDropFeedback('Only PDF files are accepted.')
                return
              }
              setDropFeedback(
                `${rejections.length} file(s) were rejected by the uploader.`
              )
            },
          }}
          helperText={`Max ${MAX_CONTRACT_FILES} PDFs, total ${Math.floor(
            MAX_TOTAL_UPLOAD_BYTES / (1024 * 1024)
          )}MB. Large uploads are automatically batched.`}
          sx={{
            '& .RaFileInput-previews': {
              maxHeight: 220,
              overflowY: 'auto',
            },
          }}
        >
          <FileField source="src" title="title" />
        </FileInput>
        <SelectionSummary
          dropFeedback={dropFeedback}
          onClearSelection={() => setDropFeedback('')}
        />
        <UploadProgressNotice />
      </SimpleForm>
    </Create>
  )
}
